import { apiClient } from '@/api/client'
import {
  PAYROLL_ME_SLIPS,
  PAYROLL_ME_CURRENT,
  PAYROLL_ME_HISTORY,
  payrollMeSlipPath,
  payrollMeSlipViewPath,
  payrollMeSlipPdfPath,
  payrollMeSlipExcelPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  CurrentSlipSummary,
  ConsolidatedPayrollItem,
  OrgPayrollBatch,
  PayrollHistoryEntry,
  SalarySlipView,
} from '@/features/payroll/types/payroll.types'

interface PaginatedData<T> {
  data: T[]
  total: number
  page: number
  limit: number
}

export async function getMyCurrentSlip(): Promise<CurrentSlipSummary> {
  const { data } = await apiClient.get<ApiResponseWrapper<CurrentSlipSummary>>(
    PAYROLL_ME_CURRENT,
  )
  return data.data
}

/** A batch slip carries the batch and its item; a live slip, its payment items. */
export type MySlipJson =
  | { source: 'org_batch'; batch: OrgPayrollBatch; item: ConsolidatedPayrollItem }
  | {
      source: 'live_payment'
      payment: { id: string; payrollMonth: { year: number; month: number }; paidAt: string }
      items: Array<Record<string, unknown>>
    }

export async function getMySlip(slipId: string): Promise<MySlipJson> {
  const { data } = await apiClient.get<ApiResponseWrapper<MySlipJson>>(payrollMeSlipPath(slipId))
  return data.data
}

export async function getMySlipView(slipId: string): Promise<SalarySlipView> {
  const { data } = await apiClient.get<ApiResponseWrapper<SalarySlipView>>(
    payrollMeSlipViewPath(slipId),
  )
  return data.data
}

export async function listMySlips(
  params?: { page?: number; limit?: number },
): Promise<PaginatedData<PayrollHistoryEntry>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedData<PayrollHistoryEntry>>>(
    PAYROLL_ME_SLIPS,
    { params },
  )
  return data.data
}

export async function listMyHistory(
  params?: { page?: number; limit?: number },
): Promise<PaginatedData<PayrollHistoryEntry>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedData<PayrollHistoryEntry>>>(
    PAYROLL_ME_HISTORY,
    { params },
  )
  return data.data
}

export async function downloadMySlipPdf(slipId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(payrollMeSlipPdfPath(slipId), {
    responseType: 'blob',
  })
  return data
}

export async function downloadMySlipExcel(slipId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(payrollMeSlipExcelPath(slipId), {
    responseType: 'blob',
  })
  return data
}
